import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Checklist, IListItem } from '../../../../AptorAdmin/Components/Checklist/Checklist.List/Checklist.List';
import { useAptorApi } from '../../../../../Api';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { SelectOption, TypedFormFieldValue } from '../../../../../Components/Form/Form.types';
import { AptorApi, IGridQueryRequest, OrderDirection, PropertyError } from '../../../../../Api/AptorApi';
import { BackButton, Loading, Panel } from '../../../../../Components';
import SelectField from '../../../../../Components/Field/SelectField';
import { useApiValidationSnackbar } from '../../../../../Api/useValidationSnackbar';
import { ChecklistForm } from '../../../../AptorAdmin/Components/Checklist/Checklist.Form';
import { useStyles } from '../../../LawPortal.styles';
import { StepButtonBar } from '../OtherRequirementWizard/StepButtonBar';

interface IRequirementChecklist {
  requirement: string;
  items: IListItem[];
}
interface ICompanyUnitApiResult {
  id: number;
  name: string;
}
type GetCompanyUnitName = (formatMessage: any, isSingular: boolean) => string;

const useChecklistSpacingStyles = makeStyles(() => ({
  checklistWrapper: {
    '& .MuiList-root': {
      marginBottom: '0rem',
    },
    '& .MuiList-root > div': {
      marginBottom: 12,
    },
    '& .MuiList-root > div:last-child': {
      paddingBottom: '0rem',
      marginBottom: 0,
    },
  },
}));

export const RequirementComplianceQuestions = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = parseInt(idParam || '0');
  const { api, abortController } = useAptorApi();
  const { getCompanyUnitName } = useContext(UserContext);
  const [companyUnits, setCompanyUnits] = useState<SelectOption[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<SelectOption[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(true);

  useEffect(() => {
    const request: IGridQueryRequest = { page: 0, pageSize: 100, orderBy: 'name', orderDirection: OrderDirection.Ascending };
    api.searchRequirementCompanyUnits<ICompanyUnitApiResult>(id, request).then((result) => {
      if (abortController.current.signal.aborted) return;
      const options: SelectOption[] = (result?.data || []).map((unit :any) => ({ label: unit.name, value: unit.id }));
      setCompanyUnits(options);
      if (options.length === 1) {
        setSelectedUnits(options);
      }
      setLoadingUnits(false);
    });
  }, [id]);
  const handleUnitSelect = useCallback((units: SelectOption | SelectOption[]) => {
    setSelectedUnits(Array.isArray(units) ? units : [units]);
  }, []);
  const shouldBackToUnitSelection = companyUnits.length > 1 && selectedUnits.length > 0;
  if (loadingUnits) {
    return <Loading />;
  }
  return (
    <>
      <Grid item xs={12}>
        {shouldBackToUnitSelection ? (
          <BackButton disableMargin link="" onclick={() => setSelectedUnits([])} />
        ) : (
          <BackButton disableMargin link="/law-portal/other-requirements/" />
        )}
      </Grid>
      <Grid item md={9} xs={12}>
        {selectedUnits.length === 0 ? (
          <CompanyUnitSelection options={companyUnits} onNext={handleUnitSelect} getCompanyUnitName={getCompanyUnitName} />
        ) : (
          <ChecklistEditor
            reqId={id}
            unitId={selectedUnits[0].value as number}
            selectedUnits={selectedUnits}
            allCompanyUnits={companyUnits}
            getCompanyUnitName={getCompanyUnitName}
          />
        )}
      </Grid>
    </>
  );
};
const CompanyUnitSelection = ({
  options,
  onNext,
  getCompanyUnitName,
}: {
  options: SelectOption[];
  onNext: (units: SelectOption | SelectOption[]) => void;
  getCompanyUnitName: GetCompanyUnitName;
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const [selected, setSelected] = useState<SelectOption | SelectOption[]>();
  const handleChange = useCallback(
    (value: TypedFormFieldValue<number> | TypedFormFieldValue<number>[] | undefined) => {
      setSelected(value as SelectOption | SelectOption[] | undefined);
    },
    [],
  );
  const hasSelection = !!selected && (!Array.isArray(selected) || selected.length > 0);
  return (
    <Panel autoHeight>
      <Box className={classes.unitSelectionRow}>
        <FormControl className={classes.unitSelectControl}>
          <SelectField
            name="companyUnit"
            placeholder={formatMessage(
                  { id: 'law-portal.manage.update-laws.checklist.companyUnit.placeholder' },
                  { alias: getCompanyUnitName(formatMessage, false) },
                )}
            options={options}
            errorTexts={[]}
            initialState={selected}
            onChange={handleChange}
          />
        </FormControl>
        <Box className={classes.nextButtonOffset}>
          <StepButtonBar
            onCancel={() => {}}
            onNext={() => selected && onNext(selected)}
            nextLabel="form.next"
            nextDisabled={!hasSelection}
            formatMessage={formatMessage}
            showBack={false}
          />
        </Box>
      </Box>
    </Panel>
  );
};
const ChecklistEditor = ({
  reqId,
  unitId,
  selectedUnits,
  allCompanyUnits,
  getCompanyUnitName,
}: {
  reqId: number;
  unitId: number;
  selectedUnits: SelectOption[];
  allCompanyUnits: SelectOption[];
  getCompanyUnitName: GetCompanyUnitName;
}) => {
  const { formatMessage } = useIntl();
  const { api, abortController } = useAptorApi();
  const { notifySuccess, notifyUnhandledException, notifyValidationErrors } = useApiValidationSnackbar();
  const checklistSpacingClasses = useChecklistSpacingStyles();
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [law, setLaw] = useState<string>();
  const [items, setItems] = useState<IListItem[]>([]);
  const [errors, setErrors] = useState<PropertyError[]>([]);
  const [additionalUnits, setAdditionalUnits] = useState<SelectOption[]>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const showAdditionalUnitsDropdown = allCompanyUnits.length > 1 && selectedUnits.length === 1;
  const additionalUnitOptions = useMemo(() => showAdditionalUnitsDropdown
    ? allCompanyUnits.filter((unit) => unit.value !== selectedUnits[0].value) : [],
    [showAdditionalUnitsDropdown, allCompanyUnits, selectedUnits],
  );
  const additionalUnitNames = useMemo(() => additionalUnits.map((unit) => unit.label).join(', '),[additionalUnits]);
  const handleAdditionalUnitsChange = useCallback((value: TypedFormFieldValue<number> | TypedFormFieldValue<number>[] | undefined) => {
    const next = value ? (Array.isArray(value) ? value : [value]) : [];
    setAdditionalUnits(next as SelectOption[]);
  },[]);

  useEffect(() => {
    setLoading(true);
    api.getRequirementChecklist<IRequirementChecklist>(reqId, unitId).then((checklist) => {
      if (abortController.current.signal.aborted) return;
      setLaw(checklist.requirement);
      setItems(checklist.items ?? []);
      setLoading(false);
    });
  }, [reqId, unitId]);

  const addQuestion = useCallback(
    (name: string) => setItems((prev) => [...prev, { name, type: 'question', groupItems: [] }]),
    [],
  );
  const addGroup = useCallback(
    (name: string) => setItems((prev) => [...prev, { name, type: 'group', groupItems: [] }]),
    [],
  );

  const enableSave = useMemo(() => items.some((i) => i.type === 'question' || i.groupItems.length > 0), [items]);
  const saveChecklist = useCallback(async () => {
    // CONFIRMED VIA TESTING: sending items with IDs that belong to the ORIGINAL
    // unit's database rows to a DIFFERENT (additional) unit causes the backend to
    // drop most of those items — only truly id-less items have a chance of being
    // saved, since the incoming IDs collide with / don't match rows that exist for
    // that other unit. So for additional units we must strip every id, including
    // inside nested groupItems, so the backend treats the whole checklist as brand
    // new content for that unit instead of trying (and failing) to match existing rows.
    const stripIds = (list: IListItem[]): IListItem[] =>
      list.map((item) => {
        const { id, ...rest } = item;
        return {
          ...rest,
          groupItems: item.groupItems.map((child) => {
            const { id: childId, ...childRest } = child;
            return childRest;
          }),
        } as IListItem;
      });

    const itemsForOriginalUnit = items;
    const itemsForAdditionalUnits = stripIds(items);

    const action = async (i: AptorApi) => {
      await i.editRequirementChecklist(reqId, selectedUnits[0].value as number, { items: itemsForOriginalUnit });

      for (const unit of additionalUnits) {
        await i.editRequirementChecklist(reqId, unit.value as number, { items: itemsForAdditionalUnits });
      }
      notifySuccess();
    };
    const onValidation = (errs: PropertyError[]) => {
      notifyValidationErrors(errs);
      setErrors(errs);
    };
    setIsSaving(true);
    try {
      await api.invoke(action, abortController.current, onValidation, notifyUnhandledException);
    } finally {
      setIsSaving(false);
    }
  }, [items, additionalUnits, selectedUnits, reqId]);
  const handleSaveClick = useCallback(() => {
    if (additionalUnits.length > 0) {
      setConfirmDialogOpen(true);
    } else {
      saveChecklist();
    }
  }, [additionalUnits, saveChecklist]);

  const handleConfirmSave = useCallback(async () => {
    setConfirmDialogOpen(false);
    await saveChecklist();
  }, [saveChecklist]);
  const closeConfirmDialog = useCallback(() => setConfirmDialogOpen(false), []);
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <>
      <Panel autoHeight titleKey="admin.law-portal.law.checklist.heading" titleValues={{ name: law }}>
        <ChecklistForm addGroup={addGroup} addQuestion={addQuestion} errors={errors} />
      </Panel>
      <Panel autoHeight titleKey="admin.law-portal.law.checklist.list.title" style={{ paddingBottom: 0 }}>
        <Box className={checklistSpacingClasses.checklistWrapper}>
          <Checklist items={items} updateItems={setItems} />
        </Box>
        <Grid
          container
          justify={showAdditionalUnitsDropdown ? 'space-between' : 'flex-end'}
          alignItems="center"
          spacing={2}
        >
          {showAdditionalUnitsDropdown && (
            <Grid item xs={12} sm={6} style={{ minWidth: 260 }}>
              <FormControl fullWidth>
                <SelectField
                  name="additionalCompanyUnits"
                  isMulti
                  placeholder={formatMessage(
                    { id: 'law-portal.manage.update-requirements.dialog.companyUnit.placeholder' },
                    { alias: getCompanyUnitName(formatMessage, false) },
                  )}
                  options={additionalUnitOptions}
                  errorTexts={[]}
                  initialState={additionalUnits}
                  onChange={handleAdditionalUnitsChange}
                />
              </FormControl>
            </Grid>
          )}
          <Grid item>
            <StepButtonBar
              onCancel={() => {}}
              onNext={handleSaveClick}
              nextLabel="form.save"
              nextDisabled={!enableSave || isSaving}
              isLoading={isSaving}
              formatMessage={formatMessage}
              showBack={false}
            />
          </Grid>
        </Grid>
      </Panel>

      <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog} aria-labelledby="confirm-overwrite-dialog-title">
        <DialogTitle id="confirm-overwrite-dialog-title">
          <FormattedMessage id="law-portal.manage.checklist.overwriteDialog.title" defaultMessage="Confirm Checklist Update" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage
              id="law-portal.manage.checklist.overwriteDialog.body"
              defaultMessage="This will overwrite the existing checklist for {unitNames}. This cannot be undone."
              values={{ unitNames: additionalUnitNames }}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} color="default">
            <FormattedMessage id="form.cancel" defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleConfirmSave} color="primary" variant="contained" autoFocus>
            <FormattedMessage id="law-portal.manage.checklist.overwriteDialog.confirm" defaultMessage="Save & Overwrite" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};